import React, { useState, useEffect } from 'react';
import './ClientDetails.css';
import Card from '../common/Card';
import Pagination from '../common/Pagination';
import { auth } from '../../services/api';

interface Client {
  id: number;
  client_name: string;
  client_acronym?: string;
  client_type: string;
  client_website?: string;
  primary_contact: string;
  contact_title?: string;
  email: string;
  phone: string;
  address: string;
  address2?: string;
  city: string;
  state: string;
  zip_code: string;
  active: boolean;
  jump_client_id?: string;
  property_count?: number;
  account_count?: number;
  created_at?: string;
  updated_at?: string;
  contacts?: Array<{
    contact_type: string;
    contact_title?: string;
    contact_name: string;
    contact_email?: string;
    contact_phone?: string;
    website?: string;
  }>;
}

interface Property {
  id: number;
  property_name: string;
  service_point_name?: string;
  jump_service_point_id?: string;
  property_address?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  utility_count?: number;
  active_accounts?: number;
  active: boolean;
  jump_property_seq_no?: number;
  jump_property_id?: string;
}

interface ClientDetailsProps {
  clientId: number;
  onBack: () => void;
  onEditClient: (client: Client) => void;
  onShowPropertyDetails: (clientId: number, propertyId: number) => void;
  onAddProperty: (clientId: number) => void;
  onEditProperty: (clientId: number, propertyId: number) => void;
  refreshTrigger?: number;
  propertyRefreshTrigger?: number;
}

const ClientDetails: React.FC<ClientDetailsProps> = ({
  clientId,
  onBack,
  onEditClient,
  onShowPropertyDetails,
  onAddProperty,
  onEditProperty,
  refreshTrigger,
  propertyRefreshTrigger
}) => {
  const [client, setClient] = useState<Client | null>(null);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [propertiesPage, setPropertiesPage] = useState(1);
  const [propertiesPerPage] = useState(12); // 2 rows × 6 columns
  const [propertySearch, setPropertySearch] = useState('');

  useEffect(() => {
    fetchClient();
    fetchProperties();
  }, [clientId, refreshTrigger]);

  useEffect(() => {
    if (propertyRefreshTrigger && propertyRefreshTrigger > 0) {
      fetchProperties();
    }
  }, [propertyRefreshTrigger]);

  useEffect(() => {
    setPropertiesPage(1);
  }, [propertySearch]);

  const fetchClient = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/clients/${clientId}`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setClient(data);
      }
    } catch (error) {
      console.error('Error fetching client:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    setPropertiesLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/clients/${clientId}/properties`, {
        headers: { ...auth.getAuthHeaders() }
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setPropertiesLoading(false);
    }
  };

  // Filter and pagination logic for properties
  const filteredProperties = properties.filter(property => {
    if (!propertySearch.trim()) return true;
    const search = propertySearch.toLowerCase();
    return (
      (property.service_point_name?.toLowerCase().includes(search)) ||
      (property.property_name?.toLowerCase().includes(search)) ||
      (property.property_address?.toLowerCase().includes(search)) ||
      (property.city?.toLowerCase().includes(search)) ||
      (property.zip_code?.toLowerCase().includes(search)) ||
      (property.jump_service_point_id?.toLowerCase().includes(search))
    );
  });
  const totalPropertiesPages = Math.ceil(filteredProperties.length / propertiesPerPage);
  const startPropertiesIndex = (propertiesPage - 1) * propertiesPerPage;
  const paginatedProperties = filteredProperties.slice(startPropertiesIndex, startPropertiesIndex + propertiesPerPage);

  const formatAddress = (client: Client) => {
    const lines = [];
    if (client.address) lines.push(client.address);
    if (client.address2) lines.push(client.address2);
    if (client.city && client.state) {
      lines.push(`${client.city}, ${client.state} ${client.zip_code || ''}`.trim());
    }
    return lines;
  };

  const getClientInitials = (client: Client) => {
    return client.client_acronym || client.client_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (!client) {
    if (loading) {
      return <div className="loading">Loading client details...</div>;
    }
    return <div className="error">Client not found</div>;
  }

  const addressLines = formatAddress(client);

  return (
    <div className="client-details">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <button type="button" onClick={onBack} className="breadcrumb-pill">
          Clients
        </button>
        <span className="breadcrumb-separator">/</span>
        <button type="button" className="breadcrumb-pill active">
          Client Details
        </button>
      </div>

      {/* Client Information Card */}
      <Card style={{marginBottom: '1rem'}}>
        {/* Client Header with Edit */}
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', paddingBottom: '0.75rem'}}>
          <h1 style={{fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
            <div style={{width: '2rem', height: '2rem', background: '#0066cc', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: getClientInitials(client).length > 2 ? '0.625rem' : '0.75rem', fontWeight: 600}}>
              {getClientInitials(client)}
            </div>
            {client.client_name}
          </h1>
          <button type="button" className="btn-edit" onClick={() => onEditClient(client)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Edit Client
          </button>
        </div>

        {/* Client Details Grid - Inline Styles */}
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1.5rem'}}>
          {/* Client Information Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h6m-6 4h6m-6 4h6" />
              </svg>
              INFORMATION
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg className="icon-type" style={{width: '0.875rem', height: '0.875rem', flexShrink: 0}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H3m2 0h3M9 7h6m-6 4h6m-6 4h6" />
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Client type">{client.client_type}</span>
            </div>
            {client.jump_client_id && (
              <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
                <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#6b7280'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
                <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="JUMP client ID">{client.jump_client_id}</span>
              </div>
            )}
            {client.client_website && (
              <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
                <svg className="icon-website" style={{width: '0.875rem', height: '0.875rem', flexShrink: 0}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                <a href={client.client_website.startsWith('http') ? client.client_website : `https://${client.client_website}`} target="_blank" rel="noopener noreferrer" style={{fontSize: '0.75rem', fontWeight: 500, color: '#0066cc', textDecoration: 'none'}} data-tooltip="Website">
                  {client.client_website}
                </a>
              </div>
            )}
          </div>

          {/* Address Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              ADDRESS
            </div>
            <div style={{display: 'flex', alignItems: 'flex-start', gap: '0.6875rem'}}>
              <svg
                className="clickable-icon"
                style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, marginTop: '0.125rem'}}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                onClick={() => {
                  if (addressLines.length > 0) {
                    const fullAddress = addressLines.join(', ');
                    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`, '_blank');
                  }
                }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <div style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937', lineHeight: 1.4}} data-tooltip="Click icon to open in maps">
                {addressLines.length > 0 ? addressLines.map((line, i) => <div key={i}>{line}</div>) : <span style={{color: '#9ca3af', fontStyle: 'italic'}}>No address</span>}
              </div>
            </div>
          </div>

          {/* Contacts Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              CONTACTS
            </div>
            {client.contacts && client.contacts.length > 0 ? (
              client.contacts.map((contact, index) => (
                <div key={index} style={{display: 'flex', alignItems: 'flex-start', gap: '0.6875rem'}}>
                  <svg className="icon-contact" style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, marginTop: '0.125rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <div style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip={contact.contact_type ? contact.contact_type.charAt(0).toUpperCase() + contact.contact_type.slice(1) + ' contact' : `Contact ${index + 1}`}>
                    {contact.contact_name}
                    {contact.contact_title && <span style={{color: '#9ca3af', fontWeight: 'normal', fontStyle: 'italic'}}> ({contact.contact_title})</span>}
                  </div>
                </div>
              ))
            ) : (
              <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
                <svg className="icon-contact" style={{width: '0.875rem', height: '0.875rem', flexShrink: 0}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span style={{fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic'}}>No contacts</span>
              </div>
            )}
          </div>

          {/* Status Section */}
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.6875rem'}}>
            <div style={{fontSize: '0.75rem', fontWeight: 600, color: '#0066cc', textTransform: 'uppercase', letterSpacing: '0.05em', paddingBottom: '0.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '0.25rem'}}>
              <svg style={{width: '0.75rem', height: '0.75rem'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9 12l2 2 4-4"/>
              </svg>
              STATUS
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg className={client.active ? 'icon-status-active' : 'icon-status-inactive'} style={{width: '0.875rem', height: '0.875rem', flexShrink: 0}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                {client.active ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9l-6 6M9 9l6 6" />
                )}
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Client status">{client.active ? 'Active' : 'Inactive'}</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#22c55e'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Created on">
                {client.created_at ? new Date(client.created_at).toLocaleDateString('en-US') : 'Not available'}
              </span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.6875rem'}}>
              <svg style={{width: '0.875rem', height: '0.875rem', flexShrink: 0, color: '#f59e0b'}} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span style={{fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}} data-tooltip="Last updated">
                {client.updated_at ? new Date(client.updated_at).toLocaleDateString('en-US') : 'Not available'}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Properties Section */}
      <Card style={{marginBottom: '1rem'}}>
        <div className="section-header">
          <h2>Properties ({filteredProperties.length}{propertySearch && ` of ${properties.length}`})</h2>
          <div style={{display: 'flex', gap: '0.75rem', alignItems: 'center'}}>
            <input
              type="text"
              placeholder="Search properties..."
              value={propertySearch}
              onChange={(e) => setPropertySearch(e.target.value)}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.375rem',
                fontSize: '0.875rem',
                width: '15rem',
                outline: 'none'
              }}
            />
            <button type="button" className="btn-primary" onClick={() => onAddProperty(clientId)}>
              + Add Property
            </button>
          </div>
        </div>

        <div className="properties-grid-six-col">
          {propertiesLoading ? (
            <div className="loading-tile">Loading properties...</div>
          ) : properties.length > 0 ? (
            <>
              {paginatedProperties.sort((a, b) => (a.jump_property_seq_no || 0) - (b.jump_property_seq_no || 0)).map((property) => (
                <div
                  key={property.id}
                  className="property-tile clickable"
                  onClick={() => onShowPropertyDetails(clientId, property.id)}
                >
                  <div style={{textAlign: 'left', marginBottom: '0.5rem', fontSize: '0.75rem', fontWeight: 500, color: '#1f2937'}}>
                    <span>{property.service_point_name || property.property_name || 'Unnamed Property'}</span>
                    {property.jump_service_point_id && (
                      <span style={{fontSize: '0.75rem', fontWeight: 'normal', fontStyle: 'italic', color: '#9ca3af'}}> ({property.jump_service_point_id})</span>
                    )}
                  </div>
                  <div className="property-address" style={{textAlign: 'left', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', fontSize: '0.6875rem', color: '#6b7280'}}>
                    {property.property_address ? (
                      <>
                        <div style={{textAlign: 'left'}}>
                          {property.property_address}
                          {property.address2 && ` ${property.address2}`}
                        </div>
                        {property.city && (
                          <div style={{textAlign: 'left', color: '#9ca3af'}}>
                            {property.city}, {property.state} {property.zip_code}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={{color: '#9ca3af', fontStyle: 'italic'}}>
                        No address available
                      </div>
                    )}
                  </div>
                  <div className="property-stats">
                    <span className="utility-count">
                      {property.utility_count || 0} utilities
                    </span>
                    {!property.active && (
                      <span className="inactive-badge">Inactive</span>
                    )}
                  </div>
                </div>
              ))}

              {filteredProperties.length > propertiesPerPage && (
                <div style={{gridColumn: '1 / -1', marginTop: '1.5rem'}}>
                  <Pagination
                    currentPage={propertiesPage}
                    totalPages={totalPropertiesPages}
                    rowsPerPage={propertiesPerPage}
                    totalItems={filteredProperties.length}
                    onPageChange={setPropertiesPage}
                    onRowsPerPageChange={() => {}} // Keep rows per page fixed
                  />
                </div>
              )}
            </>
          ) : (
            <div style={{gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: '#6c757d'}}>
              No properties found
            </div>
          )}
        </div>
      </Card>

    </div>
  );
};

export default ClientDetails;